import { StructuredJsonFieldDef } from '../../shared/design-system/structured-json-form.model';
import {
  emptyStructuredJsonItem,
  parseStructuredJsonArray,
  serializeStructuredJsonArray,
  structuredJsonItemFromPayload
} from '../../shared/design-system/structured-json-form.utils';

export const INSURANCE_HEALTH_COVERED_SERVICE_FIELDS: StructuredJsonFieldDef[] = [
  {
    key: 'code',
    type: 'string',
    labelKey: 'insurance.health.schemas.coveredService.code',
    required: true
  },
  {
    key: 'requiresPreAuth',
    type: 'boolean',
    labelKey: 'insurance.health.schemas.coveredService.requiresPreAuth'
  },
  {
    key: 'tariffCap',
    type: 'number',
    labelKey: 'insurance.health.schemas.coveredService.tariffCap'
  }
];

export const INSURANCE_GROUP_BULK_ROW_SCALAR_FIELDS: StructuredJsonFieldDef[] = [
  {
    key: 'rowKey',
    type: 'string',
    labelKey: 'insurance.group.schemas.bulkRow.rowKey',
    required: true
  },
  {
    key: 'memberPartyId',
    type: 'party',
    labelKey: 'insurance.group.schemas.bulkRow.memberPartyId',
    required: true
  },
  {
    key: 'certificateNumber',
    type: 'string',
    labelKey: 'insurance.group.schemas.bulkRow.certificateNumber'
  },
  {
    key: 'planCode',
    type: 'string',
    labelKey: 'insurance.group.schemas.bulkRow.planCode'
  }
];

export const INSURANCE_CENSUS_REPORT_FIELDS: StructuredJsonFieldDef[] = [
  {
    key: 'masterPolicyNumber',
    type: 'string',
    labelKey: 'insurance.group.schemas.census.masterPolicyNumber'
  },
  {
    key: 'activeCount',
    type: 'number',
    labelKey: 'insurance.group.schemas.census.activeCount'
  },
  {
    key: 'terminatedCount',
    type: 'number',
    labelKey: 'insurance.group.schemas.census.terminatedCount'
  },
  {
    key: 'totalCount',
    type: 'number',
    labelKey: 'insurance.group.schemas.census.totalCount'
  }
];

export interface InsuranceBulkChangeRowDraft {
  rowKey: string;
  memberPartyId: string;
  certificateNumber: string;
  coverageEffectiveFrom: string;
  coverageEffectiveTo: string;
  childPolicyId: string | null;
  terminationDateUtc: string | null;
  planCode: string | null;
}

export function parseCoveredServicesJson(json: string): Record<string, unknown>[] {
  return parseStructuredJsonArray(json);
}

export function serializeCoveredServicesJson(items: Record<string, unknown>[]): string {
  return serializeStructuredJsonArray(INSURANCE_HEALTH_COVERED_SERVICE_FIELDS, items);
}

export function coveredServiceSummary(item: Record<string, unknown>): string {
  const code = typeof item['code'] === 'string' ? item['code'] : '';
  const tariffCap = item['tariffCap'];
  const requiresPreAuth = item['requiresPreAuth'] === true || item['requiresPreAuth'] === 'true';
  const parts = [code];
  if (requiresPreAuth) {
    parts.push('pre-auth');
  }
  if (tariffCap !== null && tariffCap !== undefined && tariffCap !== '') {
    parts.push(`${tariffCap}`);
  }
  return parts.filter(value => value.trim().length > 0).join(' · ') || '-';
}

export function emptyCoveredServiceItem(): Record<string, unknown> {
  return emptyStructuredJsonItem(INSURANCE_HEALTH_COVERED_SERVICE_FIELDS);
}

export function coveredServiceItemFromPayload(payloadJson: string): Record<string, unknown> {
  return structuredJsonItemFromPayload(INSURANCE_HEALTH_COVERED_SERVICE_FIELDS, payloadJson);
}

export function emptyBulkRowDraft(index: number): InsuranceBulkChangeRowDraft {
  const model = emptyStructuredJsonItem(INSURANCE_GROUP_BULK_ROW_SCALAR_FIELDS);
  return {
    rowKey: typeof model['rowKey'] === 'string' && model['rowKey'] ? model['rowKey'] : `row-${index + 1}`,
    memberPartyId: typeof model['memberPartyId'] === 'string' ? model['memberPartyId'] : '',
    certificateNumber: typeof model['certificateNumber'] === 'string' ? model['certificateNumber'] : '',
    coverageEffectiveFrom: new Date().toISOString(),
    coverageEffectiveTo: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
    childPolicyId: null,
    terminationDateUtc: null,
    planCode: typeof model['planCode'] === 'string' && model['planCode'] ? model['planCode'] : 'STANDARD'
  };
}

export function bulkRowScalarPayloadJson(row: InsuranceBulkChangeRowDraft): string {
  return serializeStructuredJsonArray(INSURANCE_GROUP_BULK_ROW_SCALAR_FIELDS, [{
    rowKey: row.rowKey,
    memberPartyId: row.memberPartyId,
    certificateNumber: row.certificateNumber,
    planCode: row.planCode
  }]);
}

export function applyBulkRowScalarPayload(row: InsuranceBulkChangeRowDraft, payloadJson: string): InsuranceBulkChangeRowDraft {
  const model = structuredJsonItemFromPayload(INSURANCE_GROUP_BULK_ROW_SCALAR_FIELDS, payloadJson);
  return {
    ...row,
    rowKey: typeof model['rowKey'] === 'string' ? model['rowKey'] : row.rowKey,
    memberPartyId: typeof model['memberPartyId'] === 'string' ? model['memberPartyId'] : row.memberPartyId,
    certificateNumber: typeof model['certificateNumber'] === 'string' ? model['certificateNumber'] : row.certificateNumber,
    planCode: typeof model['planCode'] === 'string' ? model['planCode'] : row.planCode
  };
}

export function serializeBulkRowsJson(rows: InsuranceBulkChangeRowDraft[]): string {
  const cleaned = rows
    .filter(row => row.rowKey.trim() && row.memberPartyId.trim())
    .map(row => {
      const item: Record<string, unknown> = {
        ['rowKey']: row.rowKey.trim(),
        ['memberPartyId']: row.memberPartyId.trim()
      };
      if (row.certificateNumber.trim()) {
        item['certificateNumber'] = row.certificateNumber.trim();
      }
      if (row.coverageEffectiveFrom) {
        item['coverageEffectiveFrom'] = row.coverageEffectiveFrom;
      }
      if (row.coverageEffectiveTo) {
        item['coverageEffectiveTo'] = row.coverageEffectiveTo;
      }
      if (row.childPolicyId?.trim()) {
        item['childPolicyId'] = row.childPolicyId.trim();
      }
      if (row.terminationDateUtc) {
        item['terminationDateUtc'] = row.terminationDateUtc;
      }
      if (row.planCode?.trim()) {
        item['planCode'] = row.planCode.trim();
      }
      return item;
    });
  return JSON.stringify(cleaned);
}

export function censusReportPayloadJson(report: {
  masterPolicyNumber: string;
  activeCount: number;
  terminatedCount: number;
  totalCount: number;
}): string {
  return JSON.stringify({
    masterPolicyNumber: report.masterPolicyNumber,
    activeCount: report.activeCount,
    terminatedCount: report.terminatedCount,
    totalCount: report.totalCount
  });
}
